/**
 * The team upgrade-request modal's analytics vocabulary: the `action` token its
 * submit carries, the payload shapes its two events report, and the emit seams
 * the app binds.
 *
 * THE SUBMIT EVENT'S `screen_name` IS THE ORIGIN'S, NOT THIS SURFACE'S, and that
 * finding is what shapes this file. The modal is mounted globally and opened
 * from SEVEN places, counted over both open paths
 * (`openTeamTrialUpgradeRequestModalAtom` plus
 * `TeamTrialUpgradeRequestButton`'s own):
 *
 *   in this root      ninety_day_trial_recap_modal · clinician_trial_ended_modal
 *                     · practice_trial_ended_modal
 *   other roots       upgrade_to_clinician_modal · upgrade_to_practice_modal
 *                     · settings_billing · credits_remaining_popover
 *
 * Four of the seven belong to the billing, clinician-modal and Chat surfaces, so
 * the union is not this domain's to declare — minting `credits_remaining_popover`
 * here would put a second spelling of a Chat row in a commerce package, and
 * widening the seam to a bare `string` would delete the app's discriminated-union
 * check over the payload. So the SUBMIT SEAM CARRIES NO `screen_name` AT ALL:
 * each origin binds an emitter that names its own screen, and the port travels to
 * the modal the same way `onRequestSent` already does — through the open payload
 * of `openTeamTrialUpgradeRequestModalAtom`, and through
 * `TeamTrialUpgradeRequestButton` when the button opens the modal itself.
 *
 * That is ruling 24's port pattern doing the work the enum used to: an emit that
 * only the composition point can name is supplied by the composition point.
 *
 * The modal's OWN `modal_viewed` is unaffected — `team_trial_upgrade_request_modal`
 * is this surface's screen and stays in the shared vocabulary.
 */
import type {
  TRIALS_AND_UPGRADES_SCREEN,
  TrialPlanContext,
} from './trials-and-upgrades-analytics.types';

/**
 * The `action` token the submit carries.
 *
 * `ninety_day_trial_recap_...` on every origin, including the four that are not
 * the recap modal. FROZEN: the recap modal was the first origin and the token is
 * the warehouse key the request funnel is built on, so re-spelling it to drop the
 * prefix would be a new event rather than a rename.
 */
export const TEAM_TRIAL_UPGRADE_REQUEST_ACTION = {
  requestAdminUpgrade: 'ninety_day_trial_recap_request_admin_upgrade',
} as const;

/** `modal_viewed` on the request modal. Fired once per open. */
export type TeamTrialUpgradeRequestViewedProps = TrialPlanContext & {
  screen_name: (typeof TRIALS_AND_UPGRADES_SCREEN)['teamTrialUpgradeRequest'];
};

/**
 * `button_clicked` when the request is sent to the team admin.
 *
 * No `screen_name` — see the file docblock; the origin's emitter supplies it.
 * `is_team_member` is `true` rather than `boolean` because only a non-admin
 * member can reach this form: an admin upgrades directly and never sees it.
 */
export type TeamTrialUpgradeRequestSubmitProps = TrialPlanContext & {
  action: (typeof TEAM_TRIAL_UPGRADE_REQUEST_ACTION)['requestAdminUpgrade'];
  label: string;
  is_team_member: true;
};

/** The emit seam for the request modal becoming visible. */
export type TrackTeamTrialUpgradeRequestViewed = (
  props: TeamTrialUpgradeRequestViewedProps
) => void;

/**
 * The emit seam for the request being sent, bound by the origin that opened the
 * modal.
 *
 * Carried as a value through the open payload and through the request button's
 * props, so every origin must be able to hold one: bind a module-level constant
 * from `src/models/analytics/features/trackTeamTrialUpgradeRequestAnalytics.ts`
 * rather than building one per render. Nothing depends on its identity today,
 * but a `useCallback`-free binding is what keeps it true when the modal's form
 * grows an effect.
 */
export type TrackTeamTrialUpgradeRequestSubmit = (
  props: TeamTrialUpgradeRequestSubmitProps
) => void;
